import { useState, useEffect, useRef } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import { collection, query, where, getDocs, onSnapshot, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate, useLocation } from 'react-router-dom';
import { resolveInviteToken } from '../lib/invites';
import { addGroupMember } from '../lib/firestore/groups';
import { createRSVP } from '../lib/firestore/events';
import '../styles/NotificationBell.css';

const NotificationBell = () => {
  const { user } = useAuthState();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [readNotifications, setReadNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState('unread'); // 'unread' or 'declined'
  const [processing, setProcessing] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!user) {
      console.log('🔵 [NotificationBell] No user, not loading notifications');
      return;
    }

    console.log('🔵 [NotificationBell] Setting up notification listener for user:', user.uid);

    // Listen for notifications
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', user.uid),
      where('status', '==', 'unread')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log('🔵 [NotificationBell] Notifications snapshot:', snapshot.docs.length);
        const notifs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log('🔵 [NotificationBell] Parsed notifications:', notifs);
        
        // Separate unread and declined notifications
        const unread = notifs.filter(n => n.status === 'unread');
        const declined = notifs.filter(n => n.status === 'declined');
        
        setNotifications(unread);
        setReadNotifications(declined);
        setUnreadCount(unread.length);
      },
      (error) => {
        console.error('🔴 [NotificationBell] Error listening to notifications:', error);
      }
    );
    
    // Also listen for declined notifications
    const declinedQuery = query(
      notificationsRef,
      where('userId', '==', user.uid),
      where('status', '==', 'declined')
    );
    
    const unsubscribeDeclined = onSnapshot(
      declinedQuery,
      (snapshot) => {
        const declined = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setReadNotifications(declined);
      },
      (error) => {
        console.error('🔴 [NotificationBell] Error listening to declined notifications:', error);
      }
    );

    return () => {
      unsubscribe();
      unsubscribeDeclined();
    };
  }, [user]);

  // Close dropdown when route changes
  useEffect(() => {
    setShowDropdown(false);
  }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showDropdown]);

  const handleNotificationClick = async (notification) => {
    console.log('🔵 [NotificationBell] handleNotificationClick called:', notification);
    
    if (notification.type === 'group_sponsor') {
      // Try to get groupId from notification or resolve from invite token
      let targetGroupId = notification.groupId;
      
      if (!targetGroupId && notification.inviteToken) {
        try {
          const invite = await resolveInviteToken(notification.inviteToken);
          if (invite && invite.target.kind === 'group_join') {
            targetGroupId = invite.target.id;
          }
        } catch (error) {
          console.error('Error resolving invite token:', error);
        }
      }
      
      if (targetGroupId) {
        // Navigate to group page with notification ID for join modal
        navigate(`/groups/${targetGroupId}?notificationId=${notification.id}`);
      } else {
        console.error('🔴 [NotificationBell] No groupId found in notification');
        alert('Unable to find group information. Please try accepting from the notification.');
      }
    } else if (notification.type === 'event_invite') {
      // Try to get eventId from notification or resolve from invite token
      let targetEventId = notification.eventId;
      
      if (!targetEventId && notification.inviteToken) {
        try {
          const invite = await resolveInviteToken(notification.inviteToken);
          if (invite && invite.target.kind === 'event_join') {
            targetEventId = invite.target.id;
          }
        } catch (error) {
          console.error('Error resolving invite token:', error);
        }
      }
      
      if (targetEventId) {
        // Navigate to event page with notification ID
        navigate(`/events/${targetEventId}?notificationId=${notification.id}`);
      } else {
        console.error('🔴 [NotificationBell] No eventId found in notification');
        alert('Unable to find event information. Please try accepting from the notification.');
      }
    } else if (notification.type === 'group_invite') {
      navigate(`/groups/${notification.groupId}/preview?notificationId=${notification.id}`);
    } else if (notification.type === 'friend_request') {
      // Navigate to contacts page to see friend requests
      navigate('/contacts?tab=friends');
    }
    setShowDropdown(false);
  };

  const handleAccept = async (e, notification) => {
    console.log('🔵 [NotificationBell] handleAccept called', { notification, user: !!user, processing: processing[notification.id] });
    e.stopPropagation();
    if (!user || processing[notification.id]) {
      console.log('🔴 [NotificationBell] Early return from handleAccept', { hasUser: !!user, isProcessing: processing[notification.id] });
      return;
    }
    setProcessing({ ...processing, [notification.id]: true });

    try {
      console.log('🔵 [NotificationBell] Accepting invite for notification:', notification);
      
      if (!notification.inviteToken) {
        alert('Invalid invite token');
        setProcessing({ ...processing, [notification.id]: false });
        return;
      }

      // Resolve the invite token
      const invite = await resolveInviteToken(notification.inviteToken);
      if (!invite) {
        alert('Invite has expired or is invalid');
        await markAsRead(notification.id);
        setProcessing({ ...processing, [notification.id]: false });
        return;
      }

      console.log('🔵 [NotificationBell] Resolved invite:', invite);

      if (invite.target.kind === 'group_join') {
        // Prepare member data
        const memberData = {
          userId: user.uid,
          role: invite.metadata?.role || 'member',
          joinedAt: new Date(),
        };
        
        // Only add sponsoredBy if it's a valid string
        const sponsorId = invite.metadata?.sponsorId || invite.issuedBy;
        if (sponsorId && typeof sponsorId === 'string') {
          memberData.sponsoredBy = sponsorId;
        }
        
        console.log('🔵 [NotificationBell] Adding member with data:', memberData);
        
        // Add user to group
        await addGroupMember(invite.target.id, memberData);

        // Mark all pending notifications for this group as read
        await markAllGroupNotificationsAsRead(invite.target.id);

        // Navigate to group
        navigate(`/groups/${invite.target.id}?joined=true`);
        setShowDropdown(false);
      } else if (invite.target.kind === 'event_join') {
        // RSVP to event
        await createRSVP({
          userId: user.uid,
          eventId: invite.target.id,
          state: invite.metadata?.role === 'guest' ? 'interested' : 'going',
          plusOnes: 0,
        });

        // Mark notification as read
        await markAsRead(notification.id);

        // Navigate to event
        navigate(`/events/${invite.target.id}?joined=true`);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('🔴 [NotificationBell] Error accepting invite:', error);
      alert(`Failed to accept invite: ${error.message || 'Please try again.'}`);
    } finally {
      setProcessing({ ...processing, [notification.id]: false });
    }
  };

  const handleDecline = async (e, notification) => {
    e.stopPropagation();
    if (!user || processing[notification.id]) return;
    setProcessing({ ...processing, [notification.id]: true });

    try {
      // Mark as declined instead of read
      const notificationRef = doc(db, 'notifications', notification.id);
      await updateDoc(notificationRef, {
        status: 'declined',
        declinedAt: new Date(),
      });
      
      // Remove from unread list (will appear in declined tab)
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    } catch (error) {
      console.error('Error declining invite:', error);
    } finally {
      setProcessing({ ...processing, [notification.id]: false });
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        status: 'read',
        readAt: new Date(),
      });
      // Remove from unread list
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllGroupNotificationsAsRead = async (groupId) => {
    if (!user || !groupId) return;
    
    try {
      console.log('🔵 [NotificationBell] Marking all notifications for group as read:', groupId);
      
      // Find all unread notifications for this group
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', user.uid),
        where('groupId', '==', groupId),
        where('status', '==', 'unread')
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('🔵 [NotificationBell] No pending notifications to mark as read');
        return;
      }
      
      // Use batch write for efficiency
      const batch = writeBatch(db);
      snapshot.docs.forEach((docSnapshot) => {
        const notificationRef = doc(db, 'notifications', docSnapshot.id);
        batch.update(notificationRef, {
          status: 'read',
          readAt: new Date(),
        });
      });
      
      await batch.commit();
      console.log(`🔵 [NotificationBell] Marked ${snapshot.docs.length} notifications as read`);
      
      // Remove from unread list
      setNotifications(prev => prev.filter(n => !(n.groupId === groupId && n.status === 'unread')));
    } catch (error) {
      console.error('🔴 [NotificationBell] Error marking group notifications as read:', error);
    }
  };

  if (!user) return null;

  return (
    <div className={`notification-bell-wrapper ${showDropdown ? 'show-dropdown' : ''}`} ref={dropdownRef}>
      <button
        className="btn-icon-flat notification-icon"
        onClick={() => setShowDropdown(!showDropdown)}
        title="Notifications"
        style={{ fontSize: '1.5rem', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        📬
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <>
          {/* Backdrop for all screen sizes */}
          <div 
            className="dropdown-backdrop"
            onClick={() => setShowDropdown(false)}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => {
              e.preventDefault();
              setShowDropdown(false);
            }}
          />
          <div 
            className="notification-dropdown"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
          <div className="notification-dropdown-header">
            <div className="notification-header-top">
              <h3>Notifications</h3>
              <button
                className="notification-close-btn"
                onClick={() => setShowDropdown(false)}
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDropdown(false);
                }}
                type="button"
                title="Close"
                aria-label="Close notifications"
              >
                ✕
              </button>
            </div>
            {/* Tabs for Unread and Declined */}
            <div className="notification-tabs">
              <button
                className={`notification-tab ${activeTab === 'unread' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('unread');
                }}
                type="button"
              >
                Unread ({notifications.length})
              </button>
              <button
                className={`notification-tab ${activeTab === 'declined' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTab('declined');
                }}
                type="button"
              >
                Declined ({readNotifications.length})
              </button>
            </div>
          </div>
          {activeTab === 'unread' && (
            <>
              {notifications.length === 0 && (
                <p className="notification-empty">No new notifications</p>
              )}
              {notifications.length > 0 && (
                <div className="notification-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="notification-item"
                  onClick={(e) => {
                    console.log('🔵 [NotificationBell] Notification item clicked', { target: e.target, currentTarget: e.currentTarget });
                    // Only navigate if clicking on the notification item itself, not buttons
                    if (e.target === e.currentTarget || (e.target.closest('.notification-content') && !e.target.closest('.notification-actions') && !e.target.closest('button'))) {
                      console.log('🔵 [NotificationBell] Calling handleNotificationClick');
                      handleNotificationClick(notification);
                    } else {
                      console.log('🔵 [NotificationBell] Click ignored (button clicked)');
                    }
                  }}
                  onMouseDown={(e) => {
                    // Safari compatibility: use mousedown for better event handling
                    if (e.target === e.currentTarget || (e.target.closest('.notification-content') && !e.target.closest('.notification-actions') && !e.target.closest('button'))) {
                      e.preventDefault(); // Prevent text selection
                    }
                  }}
                  onTouchStart={(e) => {
                    // Safari mobile compatibility
                    if (e.target === e.currentTarget || (e.target.closest('.notification-content') && !e.target.closest('.notification-actions') && !e.target.closest('button'))) {
                      e.preventDefault();
                      handleNotificationClick(notification);
                    }
                  }}
                >
                  <div className="notification-content">
                    <p className="notification-title">{notification.title}</p>
                    <p className="notification-message">{notification.message}</p>
                    <span className="notification-time">
                      {notification.createdAt?.toDate
                        ? new Date(notification.createdAt.toDate()).toLocaleString()
                        : 'Just now'}
                    </span>
                    {/* Accept/Decline buttons for sponsor/invite notifications */}
                    {(notification.type === 'group_sponsor' || notification.type === 'event_invite') && (
                      <div className="notification-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn-notification-accept"
                          onClick={(e) => {
                            console.log('🔵 [NotificationBell] Accept button clicked');
                            e.preventDefault();
                            e.stopPropagation();
                            handleAccept(e, notification);
                          }}
                          onMouseDown={(e) => {
                            console.log('🔵 [NotificationBell] Accept button mousedown');
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onTouchStart={(e) => {
                            console.log('🔵 [NotificationBell] Accept button touchstart');
                            e.stopPropagation();
                          }}
                          disabled={processing[notification.id]}
                          type="button"
                        >
                          {processing[notification.id] ? '...' : 'Accept'}
                        </button>
                        <button
                          className="btn-notification-decline"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDecline(e, notification);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          disabled={processing[notification.id]}
                          type="button"
                        >
                          Decline
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className="notification-mark-read"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      markAsRead(notification.id);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    title="Mark as read"
                    type="button"
                  >
                    ✓
                  </button>
                </div>
              ))}
                </div>
              )}
            </>
          )}
          {activeTab === 'declined' && (
            <>
              {readNotifications.length === 0 && (
                <p className="notification-empty">No declined notifications</p>
              )}
              {readNotifications.length > 0 && (
                <div className="notification-list">
                  {readNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="notification-item notification-item-declined"
                      onClick={(e) => {
                        console.log('🔵 [NotificationBell] Declined notification item clicked', { target: e.target, currentTarget: e.currentTarget });
                        if (e.target === e.currentTarget || (e.target.closest('.notification-content') && !e.target.closest('.notification-actions') && !e.target.closest('button'))) {
                          console.log('🔵 [NotificationBell] Calling handleNotificationClick for declined');
                          handleNotificationClick(notification);
                        }
                      }}
                    >
                      <div className="notification-content">
                        <p className="notification-title">{notification.title}</p>
                        <p className="notification-message">{notification.message}</p>
                        <span className="notification-time">
                          {notification.createdAt?.toDate
                            ? new Date(notification.createdAt.toDate()).toLocaleString()
                            : 'Just now'}
                        </span>
                        {/* Accept button for declined notifications */}
                        {(notification.type === 'group_sponsor' || notification.type === 'event_invite') && (
                          <div className="notification-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              className="btn-notification-accept"
                              onClick={(e) => {
                                console.log('🔵 [NotificationBell] Accept button clicked (declined)');
                                e.preventDefault();
                                e.stopPropagation();
                                handleAccept(e, notification);
                              }}
                              onMouseDown={(e) => {
                                console.log('🔵 [NotificationBell] Accept button mousedown (declined)');
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              disabled={processing[notification.id]}
                              type="button"
                            >
                              {processing[notification.id] ? '...' : 'Accept'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;


